"use client";

import React from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, FileText } from "lucide-react";
import { useQuickBooksInvoice } from "@/lib/hooks/api/quickbooks/quickbooks-unified";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import {
  EnhancedTabs,
  EnhancedTabsList,
  EnhancedTabsTrigger,
  EnhancedTabsContent,
} from "@/components/ui/tabs/enhanced-tabs";
import { InvoiceDetailsHeader } from "./InvoiceDetailsHeader";
import { InvoiceOverviewTab } from "./InvoiceOverviewTab";
import { InvoiceLineItemsTab } from "./InvoiceLineItemsTab";
import { InvoiceCustomerTab } from "./InvoiceCustomerTab";
import { InvoiceStatusTab } from "./InvoiceStatusTab";
import { Building, Clock } from "lucide-react";

interface InvoiceDetailsModalProps {
  invoiceId: string | null;
  isOpen: boolean;
  onClose: () => void;
  realmId?: string; // Optional company ID for direct fetching
}

/**
 * Invoice Details Modal Component
 * Displays comprehensive invoice information in a tabbed interface
 * Coordinates data fetching and renders extracted tab components
 */
export function InvoiceDetailsModal({
  invoiceId,
  isOpen,
  onClose,
  realmId,
}: InvoiceDetailsModalProps) {
  // Only call the hook when we have a valid invoiceId to prevent caching issues
  const {
    data: invoice,
    isLoading,
    error,
  } = useQuickBooksInvoice(invoiceId || "", realmId) as {
    data: QuickBooksInvoice | undefined;
    isLoading: boolean;
    error: Error | null;
  };

  if (!invoiceId) {
    return null;
  }

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      maxWidth="7xl"
      side="bottom"
      className="h-[95vh] w-[95vw]"
    >
      {/* Header / Summary & Actions - sticky within modal for quick access */}
      <InvoiceDetailsHeader invoice={invoice} onClose={onClose} />

      {/* Scrollable Content Area */}
      <ScrollArea className="flex-1 overflow-y-auto px-1">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load invoice details: {error.message}</AlertDescription>
          </Alert>
        )}

        {invoice && (
          <div className="py-4">
            <EnhancedTabs defaultValue="overview" className="w-full">
              <EnhancedTabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-5 bg-muted/40 rounded-md p-1">
                <EnhancedTabsTrigger value="overview" variant="blue" className="py-2.5 text-sm">
                  <FileText className="h-4 w-4" />
                  Overview
                </EnhancedTabsTrigger>
                <EnhancedTabsTrigger value="line-items" variant="green" className="py-2.5 text-sm">
                  <FileText className="h-4 w-4" />
                  Line Items
                </EnhancedTabsTrigger>
                <EnhancedTabsTrigger value="customer" variant="purple" className="py-2.5 text-sm">
                  <Building className="h-4 w-4" />
                  Customer
                </EnhancedTabsTrigger>
                <EnhancedTabsTrigger value="status" variant="orange" className="py-2.5 text-sm">
                  <Clock className="h-4 w-4" />
                  Status & Delivery
                </EnhancedTabsTrigger>
              </EnhancedTabsList>

              {/* Overview Tab */}
              <EnhancedTabsContent value="overview" className="space-y-5">
                <InvoiceOverviewTab invoice={invoice} />
              </EnhancedTabsContent>

              {/* Line Items Tab */}
              <EnhancedTabsContent value="line-items" className="space-y-6">
                <InvoiceLineItemsTab invoice={invoice} />
              </EnhancedTabsContent>

              {/* Customer Tab */}
              <EnhancedTabsContent value="customer" className="space-y-4">
                <InvoiceCustomerTab invoice={invoice} />
              </EnhancedTabsContent>

              {/* Status & Delivery Tab */}
              <EnhancedTabsContent value="status" className="space-y-4">
                <InvoiceStatusTab invoice={invoice} />
              </EnhancedTabsContent>
            </EnhancedTabs>
          </div>
        )}
      </ScrollArea>
    </ResponsiveModal>
  );
}
